'use client';

import { Box } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useCampaignPageTracking } from '../../hooks/useCampaignPageTracking';
import Banner from './components/Banner';
import Section from './components/Section';
import ProductRecommend from './components/ProductRecommend';
import StyleCategory from './components/StyleCategory';
import StyleQuizDialog from './components/StyleQuizDialog';
import type { StyleHotProductClientProps } from './types';

export default function StyleHotProductClientPage({
  deviceType,
  runtimeData,
  runtimeConfig,
  isPreview,
  isAppCommunicationReady,
  inAppH5,
}: StyleHotProductClientProps) {
  const [styleQuizOpen, setStyleQuizOpen] = useState(false);
  const [styleJumpRequest, setStyleJumpRequest] = useState<{
    styleName: string;
    requestId: number;
  } | null>(null);
  const {
    trackPageView,
    trackPageLeave,
    trackNavTabClick,
    trackAdClick,
    trackAdExp,
    trackProductListPitsClick,
    trackProductListPitsExposure,
    trackStylePopupExposure,
    trackStylePopupClick,
  } = useCampaignPageTracking(runtimeConfig);

  useEffect(() => {
    if (!isAppCommunicationReady) return;

    trackPageView();

    if (inAppH5) {
      window.addEventListener('appView', trackPageView);
      window.addEventListener('appLeave', trackPageLeave);
    }

    return () => {
      trackPageLeave();
      if (inAppH5) {
        window.removeEventListener('appView', trackPageView);
        window.removeEventListener('appLeave', trackPageLeave);
      }
    };
  }, [inAppH5, isAppCommunicationReady, trackPageLeave, trackPageView]);

  const openStyleQuiz = useCallback(() => {
    setStyleQuizOpen(true);
  }, []);

  const closeStyleQuiz = useCallback(() => {
    setStyleQuizOpen(false);
  }, []);

  const handleViewStyle = useCallback((styleName: string) => {
    setStyleJumpRequest((prev) => ({
      styleName,
      requestId: (prev?.requestId || 0) + 1,
    }));
  }, []);

  const handleMarketingAdClick = useCallback(
    (index: number, extraProperties: Record<string, string>) => {
      trackAdClick('广告位版位', index, undefined, extraProperties);
    },
    [trackAdClick],
  );

  const handleMarketingAdExposure = useCallback(
    (index: number, extraProperties: Record<string, string>) => {
      trackAdExp('广告位版位', '', index, extraProperties);
    },
    [trackAdExp],
  );

  return (
    <Box className="w-full bg-white">
      <Banner deviceType={deviceType} data={runtimeData?.banner} onOpenStyleQuiz={openStyleQuiz} />

      <StyleCategory
        deviceType={deviceType}
        data={runtimeData?.style_category}
        marketingPageId={runtimeData?.marketing_page_id}
        adp={runtimeData?.adp}
        isPreview={isPreview}
        inAppH5={inAppH5}
        styleJumpRequest={styleJumpRequest}
        onTrackNavTabClick={trackNavTabClick}
        onTrackProductClick={trackProductListPitsClick}
        onTrackProductExposure={trackProductListPitsExposure}
      />
      <Section
        deviceType={deviceType}
        data={runtimeData?.marketing}
        onTrackAdClick={handleMarketingAdClick}
        onTrackAdExposure={handleMarketingAdExposure}
      />

      <ProductRecommend
        deviceType={deviceType}
        config={runtimeData?.productRecommend}
        marketingPageId={runtimeData?.marketing_page_id}
      />
      <StyleQuizDialog
        open={styleQuizOpen}
        deviceType={deviceType}
        data={runtimeData?.styleQuiz}
        onClose={closeStyleQuiz}
        onViewStyle={handleViewStyle}
        onStylePopupExposure={trackStylePopupExposure}
        onStylePopupClick={trackStylePopupClick}
      />
    </Box>
  );
}
